import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import { View, StyleSheet } from 'react-native';
import Moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { Area, Floor, Space } from '../../../../components/Map/SetFloorsModal/FloorItem';
import { assigneeName, locationName, userGroupName } from '../../data';
import TicketFilterTag from './components/TicketFilterTag';
import { DateRange } from '../../../../redux/dateRangeSlice';
import { logEvent } from '../../../../components/LogEvent';
import {
  updateTicketsFilterAssetId,
  updateTicketsFilterAssetName,
  updateTicketsFilterAssignee,
  updateTicketsFilterCritical,
  updateTicketsFilterDateRange,
  updateTicketsFilterDone,
  updateTicketsFilterHigh,
  updateTicketsFilterInProgress,
  updateTicketsFilterLocation,
  updateTicketsFilterLow,
  updateTicketsFilterMed,
  updateTicketsFilterOther,
  updateTicketsFilterSlaOverdue,
  updateTicketsFilterSlaSort,
  updateTicketsFilterUserGroup,
  updateTicketsFilterWithoutStatus,
} from '../../../../redux/ticketsFilterSlice';
import { UserGroup, UserInstance } from '../../../../constants/Types';
import isMobile from '../../../../components/IsMobile';
import ResetFiltersButton from '../../../../components/ResetFiltersButton';
import { Building } from '../../../LocationManagement/components/AddBuildingModal';

interface TicketFilterTagsProps {
  otherPriorityFilter: boolean,
  lowPriorityFilter: boolean,
  mediumPriorityFilter: boolean,
  highPriorityFilter: boolean,
  criticalPriorityFilter: boolean,
  doneFilter?: boolean,
  inProgressFilter?: boolean,
  notStartedFilter?: boolean,
  slaSort: string,
  selectedAssignee: string,
  selectedAssetId: string,
  selectedAssetName?: string,
  selectedLocation?: string,
  slaOverdueFilter?: boolean,
  selectedUserGroupId?: string,
  range?: DateRange,
  users?: UserInstance[],
  userGroups?: UserGroup[],
  setRange?: Dispatch<SetStateAction<DateRange>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  resetAllFilters: () => void,
  setOtherPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setLowPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setMediumPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setHighPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setCriticalPriorityFilter: Dispatch<SetStateAction<boolean>>,
  setDoneFilter?: Dispatch<SetStateAction<boolean>>,
  setInProgressFilter?: Dispatch<SetStateAction<boolean>>,
  setNotStartedFilter?: Dispatch<SetStateAction<boolean>>,
  setSlaSort: Dispatch<SetStateAction<string>>,
  setSelectedAssignee: Dispatch<SetStateAction<string>>,
  setSelectedAssetId: Dispatch<SetStateAction<string>>,
  setSelectedLocation?: Dispatch<SetStateAction<string>>,
  setSelectedUserGroupId?: Dispatch<SetStateAction<string>>,
  setSlaOverdueFilter?: Dispatch<SetStateAction<boolean>>,
}

const TicketFilterTagSection = ({
  selectedLocation, lowPriorityFilter, mediumPriorityFilter, highPriorityFilter,
  criticalPriorityFilter, doneFilter, inProgressFilter, notStartedFilter, slaSort, users,
  selectedAssignee, selectedAssetId, selectedUserGroupId, range, slaOverdueFilter,
  userGroups, setRange, setLoading, resetAllFilters, otherPriorityFilter, selectedAssetName,
  setOtherPriorityFilter, setLowPriorityFilter, setMediumPriorityFilter, setHighPriorityFilter,
  setCriticalPriorityFilter, setDoneFilter, setInProgressFilter, setNotStartedFilter, setSlaSort,
  setSelectedAssignee, setSelectedAssetId, setSelectedLocation, setSelectedUserGroupId,
  setSlaOverdueFilter,
}: TicketFilterTagsProps) => {
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const [selectedFloorId, setSelectedFloorId] = useState<string>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>(null);
  const selectSlaNames = createSelector(
    (state: { selectedBuilding: Building }) => state.selectedBuilding.SlaHours,
    (slaHours) => slaHours?.map((item) => item.Name) ?? [],
  );
  const slaNames: string[] = useSelector(selectSlaNames);

  const dispatch = useDispatch();

  const anyFilterApplied = () => otherPriorityFilter || lowPriorityFilter || mediumPriorityFilter
    || highPriorityFilter || criticalPriorityFilter || doneFilter || notStartedFilter
    || inProgressFilter || !!selectedAssignee || !!selectedLocation || !!selectedAssetId
    || !!selectedUserGroupId || range?.end || slaOverdueFilter;

  useEffect(() => {
    if (!selectedLocation) {
      setSelectedFloorId(null);
      setSelectedAreaId(null);
      setSelectedSpaceId(null);
    }
    if (!(buildings && selectedBuildingId && buildings[selectedBuildingId])) return;
    Object.values(buildings[selectedBuildingId]?.floors ?? {}).forEach((floor: Floor | null) => {
      if (!floor) return;
      if (floor?.LocationId === selectedLocation) {
        setSelectedFloorId(floor.LocationId);
        setSelectedAreaId(null);
        setSelectedSpaceId(null);
      } else {
        Object.values(floor?.areas ?? {})?.forEach((area: Area | null) => {
          if (!area) return;
          if (area?.LocationId === selectedLocation) {
            setSelectedFloorId(floor.LocationId);
            setSelectedAreaId(area.LocationId);
            setSelectedSpaceId(null);
          } else {
            Object.values(area?.spaces ?? {})?.forEach((space: Space | null) => {
              if (!space) return;
              if (space?.LocationId === selectedLocation) {
                setSelectedFloorId(floor.LocationId);
                setSelectedAreaId(area.LocationId);
                setSelectedSpaceId(space.LocationId);
              }
            });
          }
        });
      }
    });
  }, [buildings, selectedBuildingId, selectedLocation]);

  return <View style={styles.container}>
    {slaSort ? <TicketFilterTag
      onPress={() => {
        setSlaSort('');
        dispatch(updateTicketsFilterSlaSort(''));
      }}
      title='Sort: '
      value={`SLA time - ${slaSort}`}
      testID='ticket-sorted-by-sla-label'
    /> : null}
    {otherPriorityFilter && <TicketFilterTag
      onPress={() => {
        setOtherPriorityFilter(false);
        dispatch(updateTicketsFilterOther(false));
      }}
      title='Priority: '
      value={slaNames[4] ?? 'Other'}
      testID='ticket-filtered-by-priority-label'
    />}
    {lowPriorityFilter && <TicketFilterTag
      onPress={() => {
        setLowPriorityFilter(false);
        dispatch(updateTicketsFilterLow(false));
      }}
      title='Priority: '
      value={slaNames[3] ?? 'Low'}
      testID='ticket-filtered-by-priority-label'
    />}
    {mediumPriorityFilter && <TicketFilterTag
      onPress={() => {
        setMediumPriorityFilter(false);
        dispatch(updateTicketsFilterMed(false));
      }}
      title='Priority: '
      value={slaNames[2] ?? 'Moderate'}
      testID='ticket-filtered-by-priority-label'
    />}
    {highPriorityFilter && <TicketFilterTag
      onPress={() => {
        setHighPriorityFilter(false);
        dispatch(updateTicketsFilterHigh(false));
      }}
      title='Priority: '
      value={slaNames[1] ?? 'High'}
      testID='ticket-filtered-by-priority-label'
    />}
    {criticalPriorityFilter && <TicketFilterTag
      onPress={() => {
        setCriticalPriorityFilter(false);
        dispatch(updateTicketsFilterCritical(false));
      }}
      title='Priority: '
      value={slaNames[0] ?? 'Critical'}
      testID='ticket-filtered-by-priority-label'
    />}
    {doneFilter && <TicketFilterTag
      onPress={() => {
        setDoneFilter(false);
        dispatch(updateTicketsFilterDone(false));
      }}
      title='Status: '
      value='Done'
      testID='ticket-filtered-by-status-label'
    />}
    {inProgressFilter && <TicketFilterTag
      onPress={() => {
        setInProgressFilter(false);
        dispatch(updateTicketsFilterInProgress(false));
      }}
      title='Status: '
      value='In progress'
      testID='ticket-filtered-by-status-label'
    />}
    {notStartedFilter && <TicketFilterTag
      onPress={() => {
        setNotStartedFilter(false);
        dispatch(updateTicketsFilterWithoutStatus(false));
      }}
      title='Status: '
      value='Reported'
      testID='ticket-filtered-by-status-label'
    />}
    {!!selectedAssignee && <TicketFilterTag
      title='Assignee: '
      value={assigneeName(selectedAssignee, users)}
      onPress={() => {
        setSelectedAssignee('');
        dispatch(updateTicketsFilterAssignee(''));
      }}
      testID='ticket-filtered-by-assignee-label'
    />}
    {!!selectedAssetId && <TicketFilterTag
      title='Asset: '
      value={selectedAssetName || selectedAssetId}
      onPress={() => {
        setSelectedAssetId('');
        dispatch(updateTicketsFilterAssetId(''));
        dispatch(updateTicketsFilterAssetName(null));
      }}
      testID='ticket-filtered-by-asset-label'
    />}
    {!!selectedUserGroupId && <TicketFilterTag
      title='User Group: '
      value={userGroupName(selectedUserGroupId, userGroups)}
      onPress={() => {
        setSelectedUserGroupId('');
        dispatch(updateTicketsFilterUserGroup(''));
      }}
      testID='ticket-filtered-by-user-group-label'
    />}
    {buildings && <>
      {selectedFloorId && <TicketFilterTag
        title='Floor: '
        value={locationName(selectedFloorId, buildings[selectedBuildingId]?.floors)}
        onPress={() => {
          setSelectedLocation('');
          dispatch(updateTicketsFilterLocation(''));
        }}
        testID='ticket-filtered-by-floor-label'
      />}
      {selectedAreaId && <TicketFilterTag
        title='Area: '
        value={locationName(selectedAreaId, buildings[selectedBuildingId]?.floors)}
        onPress={() => {
          setSelectedLocation(selectedFloorId);
          dispatch(updateTicketsFilterLocation(selectedFloorId));
        }}
        testID='ticket-filtered-by-area-label'
      />}
      {selectedSpaceId && <TicketFilterTag
        title='Space: '
        value={locationName(selectedSpaceId, buildings[selectedBuildingId]?.floors)}
        onPress={() => {
          setSelectedLocation(selectedAreaId);
          dispatch(updateTicketsFilterLocation(selectedAreaId));
        }}
        testID='ticket-filtered-by-space-label'
      />}
    </>}
    {slaOverdueFilter && <TicketFilterTag
      title='SLA Overdue'
      value={''}
      onPress={() => {
        setSlaOverdueFilter(false);
        dispatch(updateTicketsFilterSlaOverdue(false));
      }}
      testID='tickets-filtered-by-sla-overdue-label'
    />}
    {(!!range?.start && !!range?.end) && <TicketFilterTag
      title='Date: '
      value={Moment(range.start).isSame(range.end, 'date') ? `${Moment(range.start).format('ll')}`
        : `${Moment(range.start).format('ll')} - ${Moment(range.end).format('ll')}`}
      onPress={() => {
        dispatch(updateTicketsFilterDateRange(null));
        setRange({ start: null, end: null });
        setLoading(true);
      }}
      testID='ticket-filtered-by-date-label'
    />}
    {anyFilterApplied() && <ResetFiltersButton
      onPress={() => {
        logEvent('TicketFilterTagSection', 'Reset_All_Pressed');
        resetAllFilters();
      }}
      style={styles.resetFiltersButton}
    />}
  </View>;
};
const styles = StyleSheet.create({
  container: {
    marginRight: isMobile ? 24 : 53,
    marginTop: 12,
    marginBottom: isMobile ? 12 : undefined,
    marginStart: 24,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
  },
  resetFiltersButton: {
    alignSelf: 'center',
    marginStart: 12,
    marginBottom: 4,
  },
});

export default TicketFilterTagSection;
